import { useContext } from "react";
import ENV from "./ENV";
import textHelpers from "app/utils/textHelpers";

let colorHelpers = {
  hexToRgbaString(hex, a = 1.0) {
    if (hex?.startsWith("rgb(")) {
      hex = colorHelpers.rgbStringToHex(hex);
    } else if (hex?.startsWith("rgba(")) {
      hex = colorHelpers.rgbaStringToHex(hex);
    }
    let rgb = colorHelpers.hexToRgb(hex);
    if (rgb) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
    }
  },
  hexToRgb(hex = "", array = false) {
    if (!hex) hex = "";
    if (hex?.startsWith("rgb(")) {
      let obj = hex.split(",").map((x) => x.replace(/\D/g, ""));
      if (array) {
        return obj;
      } else {
        return {
          r: obj[0],
          g: obj[1],
          b: obj[2],
        };
      }
    }

    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      let obj = {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      };
      if (array) {
        return [obj.r, obj.g, obj.b];
      } else {
        return obj;
      }
    } else {
      return null;
    }
  },
  rgbStringToHex(c) {
    let hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
  },
  rgbaStringToHex(c) {
    // ex 'rgba(255,255,255,1.0)'
    let a = c.split(",").map((x) => x.replace(/\D/g, ""));
    return colorHelpers.rgbObjToHex(a[0], a[1], a[2]);
  },
  componentToHex(c) {
    let hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
  },
  rgbObjToHex(r, g, b) {
    return (
      "#" +
      colorHelpers.componentToHex(r) +
      colorHelpers.componentToHex(g) +
      colorHelpers.componentToHex(b)
    );
  },
  averageColor(colors = [], uniq = false) {
    if (uniq) colors = textHelpers.uniq(colors);
    let numbers = colors
      .map((hex) => {
        return colorHelpers.hexToRgb(hex, true);
      })
      .filter((n) => !!n);

    if (!numbers?.length) return null;

    // reduce the array by averaging all values, resulting in an average [R, G, B]
    let averages = numbers.reduce((total, amount, index, array) => {
      return total.map(function (subtotal, subindex) {
        // if we reached the last color, average it out and return the hex value
        if (index == array.length - 1) {
          let result = Math.round(
            (subtotal + amount[subindex]) / array.length,
          ).toString(16);

          // add a leading 0 if it is only one character
          return result.length == 2 ? "" + result : "0" + result;
        } else {
          return subtotal + amount[subindex];
        }
      });
    });

    // if(inspect) console.log(colors, averages)

    if (!averages?.length) return null;

    // return them as a single hex string
    // return "#" + averages.join('');
    return colorHelpers.rgbObjToHex(averages[0], averages[1], averages[2]);
  },
  modifyHex(hex = "", percent = 1.0) {
    if (!hex) return hex;
    let rgb = colorHelpers.hexToRgb(hex, true);
    if (!rgb?.length) {
      console.log("failed to parse:", hex, rgb);
      return "red";
      return hex;
    }
    rgb = rgb.map((x) => Math.floor(x * percent));
    return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
  },
  hexAsFloat(hex = "") {
    let rgba;
    if (hex.startsWith("rgba")) {
      rgba = hex;
    } else {
      rgba = colorHelpers.hexToRgbaString(hex);
    }
    return colorHelpers.rgbaAsFloat(rgba);
  },
  rgbaAsFloat(rgba = "") {
    let vals = rgba.split(",").map((x) => parseFloat(x.replace(/\D/g, "")));
    let a = vals[3];
    let avg = (vals[0] + vals[1] + vals[2]) / 3;
    let float = avg / 255;
    // console.log(rgba, vals, avg, float)
    return float;
  },
  // // Here's a quick and dirty way that probably isn't correct in any technical way, but involves less computation than converting to HSV and back (so renders quicker if that matters):
  // // Grayscale is the equivalent of calculating luminosity averaged over the RGB parts of the pixel. We can mix grayness by applying a value weighting to the gray part versus the colored part:
  // // var pixels = context.getImageData(0, 0, canvas.width, canvas.height);
  // grayscale(pixels, value){
  //     var d = pixels.data;
  //     for (var i = 0; i < d.length; i += 4) {
  //         var r = d[i];
  //         var g = d[i + 1];
  //         var b = d[i + 2];
  //         var gray = 0.2989*r + 0.5870*g + 0.1140*b; //weights from CCIR 601 spec
  //         d[i] = gray * value + d[i] * (1-value);
  //         d[i+1] = gray * value + d[i+1] * (1-value);
  //         d[i+2] = gray * value + d[i+2] * (1-value);
  //     }
  //     return pixels;
  // },
  // // So instead of adding "grayness", we can take it away and add the relevant color back in to "saturate":
  // saturate(pixels, value) {
  //     var d = pixels.data;
  //     for (var i = 0; i < d.length; i += 4) {
  //         var r = d[i];
  //         var g = d[i + 1];
  //         var b = d[i + 2];
  //         var gray = 0.2989*r + 0.5870*g + 0.1140*b; //weights from CCIR 601 spec
  //         d[i] = -gray * value + d[i] * (1+value);
  //         d[i+1] = -gray * value + d[i+1] * (1+value);
  //         d[i+2] = -gray * value + d[i+2] * (1+value);
  //         //normalize over- and under-saturated values
  //         if(d[i] > 255) d[i] = 255;
  //         if(d[i+1] > 255) d[i] = 255;
  //         if(d[i+2] > 255) d[i] = 255;
  //         if(d[i] < 0) d[i] = 0;
  //         if(d[i+1] < 0) d[i] = 0;
  //         if(d[i+2] < 0) d[i] = 0;
  //     }
  //     return pixels;
  // },
  // // Again, disclaimer that this "looks" saturated but probably in no way adheres to the technical definition of "saturation" (whatever that is); I've simply posted this in the hope that it's helpful to passers-by.

  RGBtoHSV(color) {
    let r, g, b, h, s, v;
    r = color[0];
    g = color[1];
    b = color[2];
    let min = Math.min(r, g, b);
    let max = Math.max(r, g, b);

    v = max;
    let delta = max - min;
    if (max != 0) s = delta / max;
    // s
    else {
      // r = g = b = 0        // s = 0, v is undefined
      s = 0;
      h = -1;
      return [h, s, undefined];
    }
    if (r === max) h = (g - b) / delta;
    // between yellow & magenta
    else if (g === max) h = 2 + (b - r) / delta;
    // between cyan & yellow
    else h = 4 + (r - g) / delta; // between magenta & cyan
    h *= 60; // degrees
    if (h < 0) h += 360;
    if (isNaN(h)) h = 0;
    return [h, s, v];
  },

  HSVtoRGB(color) {
    let i;
    let h, s, v, r, g, b;
    h = color[0];
    s = color[1];
    v = color[2];
    if (s === 0) {
      // achromatic (grey)
      r = g = b = v;
      return [r, g, b];
    }
    h /= 60; // sector 0 to 5
    i = Math.floor(h);
    let f = h - i; // factorial part of h
    let p = v * (1 - s);
    let q = v * (1 - s * f);
    let t = v * (1 - s * (1 - f));
    switch (i) {
      case 0:
        r = v;
        g = t;
        b = p;
        break;
      case 1:
        r = q;
        g = v;
        b = p;
        break;
      case 2:
        r = p;
        g = v;
        b = t;
        break;
      case 3:
        r = p;
        g = q;
        b = v;
        break;
      case 4:
        r = t;
        g = p;
        b = v;
        break;
      default:
        // case 5:
        r = v;
        g = p;
        b = q;
        break;
    }
    r = r.toFixed();
    g = g.toFixed();
    b = b.toFixed();
    return [r, g, b];
  },

  saturate(rgbArray, saturation = 1.0) {
    // by converting to the HSV (hue, saturation and value) format you can manually change the S component in this manner:
    // console.log(rgbArray)
    let hsv = colorHelpers.RGBtoHSV(rgbArray);
    // console.log('hsv: ', hsv)
    hsv[1] *= saturation;
    let rgb = colorHelpers.HSVtoRGB(hsv);
    // console.log(rgb)
    return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
  },

  allColors(colorScheme = "muted") {
    // dark: ["muted", "bold", "newspaper_dark"];
    // light: ["parchment", "clarity", "newspaper"];
    let palette = colorScheme;
    let palettes = {
      muted: {
        // "": "transparent",
        maroon: "#a80011",
        red: "#b33131",
        orange: "#e4723d",
        brown: "#995522",
        yellow: "#aa9e03",
        lime: "#66bb22",
        green: "#008811",
        viridian: "#44dd97",
        cyan: "#00cccc",
        blue: "#4097e4",
        navy: "#1e4d93",
        indigo: "#572aa9",
        purple: "#8e4595",
        magenta: "#d02c93",
        pink: "#efa0c4",
        cream: "#e4cea0",
        white: "#eeeeff",
        gray: "#889999",
        black: "#3f3f4f",
      },
      bold: {
        red: "#ff0000",
        maroon: "#990022",
        pink: "#ff44ee",
        orange: "#ee7722",
        yellow: "#ffff00",
        lime: "#55ff00",
        green: "#00ff00",
        viridian: "#11ee88",
        cyan: "#00ffff",
        blue: "#0000ff",
        navy: "#220099",
        indigo: "#6600cc",
        purple: "#7620dc",
        magenta: "#ff00ee",
        brown: "#995522",
        cream: "#f4deb0",
        white: "#eeeeff",
        gray: "#889999",
        black: "#222244",
      },
      newspaper_dark: {
        red: "#b76060",
        maroon: "#a43540",
        pink: "#d4a5ba",
        orange: "#df8f6b",
        yellow: "#b9b36a",
        lime: "#a9cc93",
        green: "#94c391",
        viridian: "#699683",
        cyan: "#7a94aa",
        blue: "#6b8cbf",
        navy: "#546a8a",
        indigo: "#6a5a85",
        purple: "#7d6080",
        magenta: "#a76fab",
        brown: "#846d5c",
        cream: "#b0a794",
        white: "#ababb4",
        gray: "#585858",
        black: "#111111",
      },
      parchment: {
        red: "#E14B53",
        maroon: "#9A2647",
        brown: "#D59A66",
        cream: "#DAB7AB",
        orange: "#f0724c",
        yellow: "#ECC22E",
        lime: "#9CE688",
        green: "#12B081",
        viridian: "#3EBAB8",
        cyan: "#67D1DF",
        blue: "#5FB7D6",
        navy: "#238DB0",
        indigo: "#9874bf",
        purple: "#c28ed7",
        magenta: "#f288f2",
        pink: "#FFACE5",
        // 'cream': '#FCDACB',
        white: "#C9D4D6",
        gray: "#D0BBC4",
        black: "#77658A",
      },
      clarity: {
        // based off 'purple': '#ca94e0',
        red: "#fe7877",
        maroon: "#bf827c",
        // 'pink': '#e9a0a0',
        pink: "#fdb0d7",
        orange: "#fc724c",
        yellow: "#ebda23",
        lime: "#94e95f",
        green: "#85e39d",
        viridian: "#32FCC1",
        cyan: "#77F1FF",
        // 'blue': '#8080e9',
        blue: "#4cc3ea",
        navy: "#378faa",
        indigo: "#7B3FFC",
        purple: "#8B4FFC",
        magenta: "#fb89f1",
        brown: "#dca078",
        // 'cream': '#f4deb0',
        cream: "#efcfa0",
        white: "#eeeeff",
        // 'gray': '#889999',
        gray: "#719eaf",
        black: "#556677",
      },
      light: {
        red: "#E14B53",
        maroon: "#9A2647",
        // 'pink': '#FF72C3',
        pink: "#FFACE5",
        // 'orange': '#FF8066',
        // 'orange': '#fc724c',
        orange: "#f0724c",
        yellow: "#FCD23E",
        lime: "#9CE688",
        green: "#12B081",
        viridian: "#46B055",
        cyan: "#77F1FF",
        blue: "#5FB7D6",
        navy: "#238DB0",
        indigo: "#8E54C4",
        purple: "#9E64D4",
        magenta: "#E985EC",
        brown: "#D59A66",
        // 'cream': '#FCDACB',
        cream: "#FAD7BB",
        white: "#C9D4D6",
        gray: "#D0BBC4",
        black: "#77658A",
      },
      newspaper_light: {
        red: "#b76060",
        maroon: "#a43540",
        pink: "#d4a5ba",
        orange: "#df8f6b",
        yellow: "#b9b36a",
        lime: "#a3d280",
        green: "#9bb999",
        viridian: "#699683",
        cyan: "#97bbd9",
        blue: "#6b8cbf",
        navy: "#364b6a",
        indigo: "#43355a",
        purple: "#7d6080",
        magenta: "#a76fab",
        brown: "#846d5c",
        cream: "#b0a794",
        white: "#ababb4",
        gray: "#585858",
        black: "#11111",
      },
      custom: {
        red: "#fe7877",
        maroon: "#bf827c",
        // 'pink': '#e9a0a0',
        pink: "#fdb0d7",
        orange: "#ffa070",
        // 'yellow': '#edd468',
        yellow: "#f5eb7a",
        // 'green': '#80e980',
        lime: "#94e95f",
        green: "#85e39d",
        viridian: "#5fe1b7",
        cyan: "#9fecec",
        // 'blue': '#8080e9',
        blue: "#4cc3ea",
        navy: "#378faa",
        indigo: "#d070ff",
        purple: "#c090e0",
        magenta: "#fb89f1",
        brown: "#dca078",
        // 'cream': '#f4deb0',
        cream: "#ffdfb0",
        white: "#eeeeff",
        // 'gray': '#889999',
        gray: "#719eaf",
        black: "#556677",
      },
    };
    let colors = palettes[colorScheme] || palettes["muted"];
    return colors;
  },
};

export default colorHelpers;
